import type { PublicProduct } from "@listou/shared/types/api";
import { Link } from "@tanstack/react-router";

import { formatPrice, plural } from "../lib/format";
import { FALLBACK_STICKER } from "../lib/theme";
import { Sticker } from "./list-page/parts";
import { formatRating, Stars } from "./stars";

export function ProductCard({ product, rank }: { product: PublicProduct; rank?: number }) {
  return (
    <Link
      to="/p/$slug"
      params={{ slug: product.slug }}
      className="group grid h-full content-start overflow-hidden rounded-3xl border-[1.5px] border-line bg-surface transition hover:border-ink/25"
    >
      <div className="relative grid aspect-square place-items-center bg-white">
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt=""
            loading="lazy"
            // Out of the flow: as a grid item, a tall image stretches the row and the
            // square with it (aspect-ratio is only a minimum for content-sized boxes).
            className="absolute inset-0 size-full object-contain p-5"
          />
        ) : (
          <Sticker src={FALLBACK_STICKER} className="w-1/2 object-contain" />
        )}
        {rank !== undefined && (
          <span className="absolute top-3 left-3 grid size-9 place-items-center rounded-full bg-ink font-display font-bold text-white">
            {rank}
          </span>
        )}
      </div>
      <div className="grid gap-1.5 p-4">
        <p className="line-clamp-2 font-display text-lg leading-snug font-bold group-hover:underline">
          {product.title}
        </p>
        <p className="text-muted">
          {[formatPrice(product.priceCents, product.currency), product.merchant]
            .filter(Boolean)
            .join(", ")}
        </p>
        {product.rating && (
          <p className="flex items-center gap-1.5 text-sm">
            <Stars value={product.rating.average} size="size-4" />
            <span className="font-bold">{formatRating(product.rating.average)}</span>
            <span className="text-muted">({plural(product.rating.count, "avis", "avis")})</span>
          </p>
        )}
        {product.wishCount > 0 && (
          <p className="text-sm font-bold text-accent">
            {plural(product.wishCount, "parent le veut", "parents le veulent")}
            {product.wishCount7d > 0 && `, +${product.wishCount7d} cette semaine`}
          </p>
        )}
      </div>
    </Link>
  );
}
